import type { ToolCustomizationJsonConfig } from '@magicschool/business-logic/tools';
import { logger } from '@magicschool/logger';
import { specifyModalReturnFocus, toast } from '@magicschool/spellbook';
import type { Enums, RoomBuilderTool, Tables } from '@magicschool/supabase/types';
import { DEFAULT_LOCALE } from '@magicschool/supabase/types';
import type { ToolCustomization } from 'features/rooms/types';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import type { UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayUpdate } from 'react-hook-form';
import type { IntlShape } from 'react-intl';
import { ImmutableList, ImmutableMap } from 'util/immutable';
import {
  type GetRoomByShareCodeResponse,
  getRoomByShareCode,
  updateRoom,
  updateRoomShareLink,
} from '@/app/(teacher)/magic-student/(dashboard-layout)/rooms/actions';
import type { RoomBuilderForm } from '@/app/(teacher)/magic-student/rooms/_components/RoomBuilder/FormFields/RoomBuilderFormSchema';
import { getRoomUrlAfterSubmit } from '@/components/RoomSettings/util';
import type { CreatedAckTool } from '@/features/rooms/types';
import { playTtsPreviewAudio } from '@/features/tts/playTtsPreviewAudio';
import { releaseAudioElement } from '@/features/tts/preserveAudioUserGesture';
import type { Voice } from '@/features/tts/types';
import { deleteToolTemplate as deleteToolTemplateRequest, saveToolTemplate } from '@/hooks/useToolCustomizations';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';
import { search } from '@/util/search';
import { buildStudentUrl } from './_helpers/buildStudentUrl';
import { createEmptyRoomTool } from './_helpers/createEmptyRoomTool';
import { normalizeRoomToolData } from './_helpers/normalizeRoomToolData';
import { defaultRoom, type RoomActionsModalType, type RoomBuilderError } from './types';

export type RoomActionsStore = {
  activeModal: RoomActionsModalType | null;
  activeModalRoomId?: string;
  audioElement: HTMLAudioElement | null;
  confirmRemoveModalOpen: boolean;
  copyRoomError: string;
  currentRoomToolFormId: string | null;
  deleteModalOpen: boolean;
  deleteRoomId: string;
  deleteRoomToolModalOpen: boolean;
  forcePromptErrorMessage: boolean;
  isLoadingAudio: boolean;
  joinInfoRoomId: string;
  joinInfoModalStep: 'codes' | 'options' | 'room' | 'student';
  joinInfoUrl: string;
  loading: boolean;
  modalLoading: boolean;
  modalActiveTab: 'new' | 'existing';
  toolsHeadingRef?: React.RefObject<HTMLHeadingElement | null>;
  modalSelectedExistingRooms: string[];
  modalShareCode: string | null; // For copy operations
  modalSharedRoom?: Tables<'rooms'>;
  modalToolsData: GetRoomByShareCodeResponse['tools'];
  previewDisabled: boolean;
  previewInitialized: boolean;
  recentlyViewedRoomToolIds: ImmutableList<string>;
  role: string;
  roomBuilderErrors: RoomBuilderError[];
  selectedBaseTool: RoomBuilderTool | null;
  shareDrawerLinkLoading: boolean;
  shareDrawerRoomId: string;
  showAdditionalFields: boolean;
  showNoToolsAlert: boolean;
  submittingTemplate: boolean;
  templateName: string;
  templateNameModalOpen: boolean;
  toolSearchResults: ImmutableList<RoomBuilderTool>;

  deleteToolTemplate: (tc: ToolCustomization | null, intl: IntlShape) => Promise<boolean>;
  addRoomTool: (args: { tool: RoomBuilderTool; append: UseFieldArrayAppend<RoomBuilderForm, 'roomTools'> }) => void;
  addToolTemplate: (
    currentToolCustomization: RoomBuilderForm['roomTools'][number]['toolCustomization'],
    intl: IntlShape,
  ) => Promise<boolean>;
  openJoinInfoModal: (
    room: Tables<'rooms'>,
    options?: { roomStudentId?: string; returnFocusRef?: React.RefObject<HTMLElement | null> },
  ) => void;
  openShareDrawer: (room: Tables<'rooms'>) => void;
  removeTool: (args: {
    remove: UseFieldArrayRemove;
    roomTools: RoomBuilderForm['roomTools'];
    allToolsMap: Map<string, RoomBuilderTool> | ImmutableMap<string, RoomBuilderTool>;
  }) => void;
  searchTools: (searchTerm: string, allToolsMap: Map<string, RoomBuilderTool> | ImmutableMap<string, RoomBuilderTool>) => Promise<void>;
  selectRoomTool: (
    roomToolFormId: string,
    baseToolId: string,
    allToolsMap: Map<string, RoomBuilderTool> | ImmutableMap<string, RoomBuilderTool>,
  ) => void;
  getDraftRoomByShareCode: (shareCode: string) => Promise<Partial<Tables<'rooms'>> | undefined>;
  setDraftTools: (
    roomTools: RoomBuilderForm['roomTools'],
    intl: IntlShape,
    room?: Tables<'rooms'> | null,
    onAfterSave?: (savedTools: CreatedAckTool[]) => Promise<void>,
  ) => Promise<{ success: boolean; redirectUrl?: string }>;
  setField: SetField<RoomActionsStore>;
  switchBaseTool: (args: {
    tool: RoomBuilderTool;
    index: number;
    append: UseFieldArrayAppend<RoomBuilderForm, 'roomTools'>;
    update: UseFieldArrayUpdate<RoomBuilderForm, 'roomTools'>;
  }) => void;
  testVoice: (intl: IntlShape, voice: Voice) => void;
  updateShareLinkAccessType: (accessType: Enums<'room_sharing_access'>, roomId: string, onUpdate?: (room: Tables<'rooms'>) => void) => void;
  resetRoomBuilderState: () => void;
};

const defaultState = {
  activeModal: null,
  audioElement: null,
  confirmRemoveModalOpen: false,
  copyRoomError: '',
  currentRoomToolFormId: null,
  deleteModalOpen: false,
  deleteRoomId: '',
  deleteRoomToolModalOpen: false,
  forcePromptErrorMessage: false,
  isLoadingAudio: false,
  joinInfoModalStep: 'options' as const,
  joinInfoRoomId: '',
  joinInfoUrl: '',
  loading: false,
  modalLoading: false,
  modalActiveTab: 'new' as const,
  toolsHeadingRef: undefined,
  modalShareCode: null,
  modalSelectedExistingRooms: [],
  modalToolsData: [],
  previewDisabled: true,
  previewInitialized: false,
  recentlyViewedRoomToolIds: ImmutableList<string>(),
  role: '',
  roomBuilderErrors: [],
  roomToolToCustomize: null,
  selectedBaseTool: null,
  shareDrawerLinkLoading: false,
  shareDrawerRoomId: '',
  showAdditionalFields: false,
  showNoToolsAlert: false,
  submittingTemplate: false,
  templateName: '',
  templateNameModalOpen: false,
  toolSearchResults: ImmutableList<RoomBuilderTool>(),
};

export const createRoomActionsStoreSlice = createStoreSlice('RoomActionsStoreData', defaultState, ({ set, get, setField, getFull }) => ({
  setField,
  getDraftRoomByShareCode: async (shareCode) => {
    set({
      modalLoading: true,
      copyRoomError: defaultState.copyRoomError,
      modalShareCode: shareCode,
    });

    const results = await getRoomByShareCode(shareCode);
    if (results.error) {
      logger.error(results.error.message, { error: results.error });

      let errorMessage = 'something-went-wrong';
      if (results.error.message === 'Unauthorized') {
        errorMessage = 'add-room-template.error.unauthorized';
      } else if (results.error.message === 'Room not found') {
        errorMessage = 'add-room-template.error.not-found';
      }
      set({ modalLoading: false, copyRoomError: errorMessage });
      return undefined;
    }

    analyticsTrack('StudentRooms:CopyRoomModal', { shareCode });

    const sharedRoom = { ...defaultRoom, ...results?.data, require_login: results?.data?.requireLogin || false } as Tables<'rooms'>;

    set({
      modalToolsData: results?.data?.tools || [],
      modalSharedRoom: sharedRoom,
      modalLoading: false,
    });

    return sharedRoom;
  },
  openJoinInfoModal: (room, options) => {
    const { roomStudentId, returnFocusRef } = options ?? {};

    if (returnFocusRef) {
      specifyModalReturnFocus('roomJoinInfo', returnFocusRef);
    }

    if (!room) {
      logger.error('No current room to open join info modal');
      return;
    }

    set({
      joinInfoRoomId: room.id,
      joinInfoModalStep: roomStudentId ? 'student' : 'options',
      joinInfoUrl: buildStudentUrl(room.require_login, room.join_code, roomStudentId),
    });
  },
  openShareDrawer: (room) => {
    set({
      shareDrawerRoomId: room.id,
    });
  },
  updateShareLinkAccessType: async (accessType, roomId, onUpdate) => {
    set({ shareDrawerLinkLoading: true });

    analyticsTrack('StudentRooms:UpdateShareLinkAccessType', { location: 'RoomsPage', accessType });

    const result = await updateRoomShareLink(roomId, accessType);

    if (result.error) {
      logger.error(result.error.message, { error: result.error });
      toast({ title: 'Failed to update sharing link', status: 'error' });
      set({ shareDrawerLinkLoading: false });
    }

    if (!result.data) return;

    onUpdate?.(result.data);

    set({ shareDrawerLinkLoading: false });
  },

  switchBaseTool: ({ tool, index, append, update }) => {
    const { currentRoomToolFormId, addRoomTool } = get();

    if (currentRoomToolFormId === null) {
      addRoomTool({ tool, append });
    } else {
      const roomTool = createEmptyRoomTool(tool);
      set({ currentRoomToolFormId: roomTool.roomToolFormId });
      update(index, roomTool);
    }

    set({
      selectedBaseTool: tool,
      previewDisabled: true,
      showAdditionalFields: false,
    });
  },

  selectRoomTool: (roomToolFormId, baseToolId, allToolsMap) => {
    const toolsMap = ImmutableMap.isMap(allToolsMap) ? allToolsMap : ImmutableMap(allToolsMap);
    const tool = toolsMap.get(baseToolId, null);

    set({
      currentRoomToolFormId: roomToolFormId,
      selectedBaseTool: tool,
      previewDisabled: true,
      showAdditionalFields: false,
    });
  },

  addRoomTool: ({ tool, append }) => {
    const roomTool = createEmptyRoomTool(tool);
    append(roomTool);

    set({
      selectedBaseTool: tool,
      currentRoomToolFormId: roomTool.roomToolFormId,
      previewDisabled: true,
      showAdditionalFields: false,
      showNoToolsAlert: false,
    });
  },

  removeTool: ({ remove, roomTools, allToolsMap }) => {
    const { currentRoomToolFormId, roomBuilderErrors, selectRoomTool } = get();
    if (currentRoomToolFormId !== null) {
      const index = roomTools.findIndex((rt) => rt.roomToolFormId === currentRoomToolFormId);
      remove(index);

      const count = roomTools.length;
      let newRoomToolIndex = count > 1 ? count - 2 : null;

      if (index === count - 2) {
        newRoomToolIndex = index + 1;
      }

      if (newRoomToolIndex === null) {
        set({ selectedBaseTool: null, currentRoomToolFormId: null });
      } else {
        const roomTool = roomTools[newRoomToolIndex];
        selectRoomTool(roomTool.roomToolFormId, roomTool.toolUuid, allToolsMap);
      }

      const filteredErrors = roomBuilderErrors.filter((error) => error.roomToolFormId !== currentRoomToolFormId);
      set({
        roomBuilderErrors: filteredErrors,
        deleteRoomToolModalOpen: false,
        previewDisabled: true,
        showAdditionalFields: false,
      });
    }
  },

  setDraftTools: async (roomTools, intl, room, onAfterSave) => {
    const modalToolsData = roomTools.map(normalizeRoomToolData);

    if (room?.id) {
      set({
        modalToolsData: modalToolsData,
        roomBuilderErrors: [],
      });

      const result = await updateRoom({
        id: room.id,
        name: room.name,
        description: room.description || '',
        locale: room.locale || DEFAULT_LOCALE,
        requireLogin: room.require_login || false,
        scheduling_enabled: room.scheduling_enabled || false,
        scheduled_start_at: room.scheduling_enabled ? room.scheduled_start_at : null,
        scheduled_end_at: room.scheduling_enabled ? room.scheduled_end_at : null,
        tools: modalToolsData,
      });

      if (result.error) {
        logger.error('Failed to update room tools', { error: result.error, roomId: room.id });
        toast({ title: intl.formatMessage({ id: 'toast.error.generic' }), status: 'error' });
        return { success: false };
      }

      // Call onAfterSave callback with newly created tools for character chatbot ack migration
      if (onAfterSave && result.data?.createdTools?.length) {
        try {
          await onAfterSave(result.data.createdTools);
        } catch (error) {
          logger.error('Failed to execute onAfterSave callback', {
            roomId: room.id,
            createdToolsCount: result.data.createdTools.length,
            error,
          });
        }
      }

      return {
        success: true,
        redirectUrl: getRoomUrlAfterSubmit({
          roomId: room.id,
          roomActionType: 'update',
          isLTI: getFull().AuthStoreData.isLTI,
        }),
      };
    } else {
      set({
        modalToolsData: modalToolsData,
        activeModal: 'create',
        roomBuilderErrors: [],
      });
    }

    return { success: true };
  },

  addToolTemplate: async (currentToolCustomization, intl) => {
    set({ submittingTemplate: true });

    const { templateName, selectedBaseTool } = get();
    if (!currentToolCustomization || !selectedBaseTool) {
      set({ submittingTemplate: false });
      return false;
    }

    try {
      await saveToolTemplate({
        name: templateName,
        tool_slug: selectedBaseTool.toolSlug,
        tool_uuid: currentToolCustomization.tool_uuid,
        json_config: currentToolCustomization.json_config as ToolCustomizationJsonConfig,
        source_customization_id: currentToolCustomization.id || null,
      });
    } catch {
      set({ submittingTemplate: false });
      toast({ title: intl.formatMessage({ id: 'room-settings.save-template.error' }), status: 'error' });
      return false;
    }

    set({
      submittingTemplate: false,
      templateNameModalOpen: false,
      templateName: '',
    });

    toast({ title: intl.formatMessage({ id: 'room-settings.save-template.success' }), status: 'success' });
    return true;
  },

  deleteToolTemplate: async (tc, intl) => {
    if (!tc) return false;
    try {
      await deleteToolTemplateRequest(tc.id);
    } catch {
      toast({ title: intl.formatMessage({ id: 'toast.error.generic' }), status: 'error' });
      return false;
    }

    return true;
  },

  searchTools: async (searchTerm, allToolsMap) => {
    if (!searchTerm || searchTerm.trim() === '') {
      set({ toolSearchResults: ImmutableList() });
      return;
    }

    const toolsMap = ImmutableMap.isMap(allToolsMap) ? allToolsMap : ImmutableMap(allToolsMap);

    const listToSearch = toolsMap
      .valueSeq()
      .map((tool) => ({
        id: tool.toolId,
        title: tool.title || '',
        description: tool.description || '',
      }))
      .toArray();

    const results = await search({
      listToSearch,
      searchTerm,
      options: {
        threshold: 0.3,
        keys: [
          { name: 'title', weight: 0.4 },
          { name: 'description', weight: 0.6 },
        ],
        ignoreLocation: true,
        shouldSort: true,
        includeScore: true,
      },
    });

    const filteredTools = toolsMap.filter((tool) => results.some((r) => r.item.id === tool.toolId)).valueSeq();

    set({ toolSearchResults: ImmutableList(filteredTools) });
  },

  testVoice: async (intl, voice) => {
    const { selectedBaseTool } = get();
    set({ isLoadingAudio: true });

    analyticsTrack('RoomBuilder:PreviewReadAloud', {
      toolId: selectedBaseTool?.toolSlug,
      voice: voice,
    });

    const { audioElement } = get();

    if (audioElement) {
      releaseAudioElement(audioElement);
      set({ audioElement: null });
    }

    try {
      const audio = await playTtsPreviewAudio({ voice });
      set({ audioElement: audio, isLoadingAudio: false });
    } catch {
      set({ isLoadingAudio: false });
      toast({ title: intl.formatMessage({ id: 'voice-test.error' }), status: 'error' });
      throw Error('Failed to fetch audio');
    }
  },

  /**
   * Reset room builder-specific state to prevent pollution between instances
   * Call this when unmounting RoomBuilderClient or navigating away
   */
  resetRoomBuilderState: () => {
    // Clean up audio element if playing
    const { audioElement } = get();
    if (audioElement) {
      releaseAudioElement(audioElement);
    }

    // Reset only room builder-specific state, preserve modals/drawers state
    set({
      audioElement: null,
      currentRoomToolFormId: null,
      forcePromptErrorMessage: false,
      isLoadingAudio: false,
      modalToolsData: [],
      previewDisabled: true,
      previewInitialized: false,
      roomBuilderErrors: [],
      selectedBaseTool: null,
      showAdditionalFields: false,
      showNoToolsAlert: false,
      toolSearchResults: ImmutableList(),
    });
  },
}));
